import { type RefObject, useLayoutEffect, useRef } from 'react';
import type { ChatMessage } from '@wundo/shared/chat.ts';
import { type ChatActivityStep, useFollowBottom } from '@wundo/ui';

/**
 * How close to the top counts as reaching it. A page is asked for before the
 * reader arrives, so the conversation grows upward under them rather than
 * stopping dead while it loads.
 */
const NEAR_TOP_PX = 120;

export interface ConversationScrollOptions {
  messages: ChatMessage[];
  streaming: readonly { id: string; text: string }[];
  typing: boolean;
  activitySteps: readonly ChatActivityStep[];
  hasOlder: boolean;
  loadingOlder: boolean;
  onLoadOlder: () => void;
}

export interface ConversationScroll {
  scroller: RefObject<HTMLDivElement | null>;
  onScroll: () => void;
  /** Follow a reply down as it grows, for whoever was already at the bottom. */
  followGrowth: () => void;
}

/**
 * Where the reader is in a conversation: kept at the bottom while it grows,
 * and kept on the same words when an older page is put on top.
 */
export function useConversationScroll({
  messages,
  streaming,
  typing,
  activitySteps,
  hasOlder,
  loadingOlder,
  onLoadOlder,
}: ConversationScrollOptions): ConversationScroll {
  const scroller = useRef<HTMLDivElement>(null);
  /* The height the conversation had just before a page was put on top of it. */
  const heightBeforeOlder = useRef<number | null>(null);
  const newest = messages.at(-1)?.id;
  const oldest = messages[0]?.id;
  const streamedLength = streaming.reduce((total, reply) => total + reply.text.length, 0);
  const activityKey = activitySteps.map((step) => `${step.id}:${step.state}`).join(',');

  /*
   * A reply being written grows without the conversation gaining a message,
   * so how much of it there is says there is more to follow down to.
   */
  const { followGrowth } = useFollowBottom(
    scroller,
    `${newest}|${typing}|${activityKey}|${messages.length}|${streamedLength}`,
    messages.length > 0,
  );

  /*
   * An older page lands above whatever is on screen, which would otherwise
   * push it down by exactly the height of what was added. Putting that height
   * back leaves the reader looking at the same words they were.
   */
  useLayoutEffect(() => {
    const element = scroller.current;
    const before = heightBeforeOlder.current;

    if (!element || before === null) {
      return;
    }

    element.scrollTop += element.scrollHeight - before;
    heightBeforeOlder.current = null;
  }, [oldest]);

  function onScroll() {
    const element = scroller.current;

    if (!element) {
      return;
    }

    if (!hasOlder || loadingOlder || element.scrollTop > NEAR_TOP_PX) {
      return;
    }

    heightBeforeOlder.current = element.scrollHeight;
    onLoadOlder();
  }

  return { scroller, onScroll, followGrowth };
}
